import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	OnDestroy,
	OnInit,
	ViewChild,
	ViewEncapsulation,
} from "@angular/core";
import { UntypedFormBuilder, Validators } from "@angular/forms";
import { MatLegacyDialogRef as MatDialogRef } from "@angular/material/legacy-dialog";
import { AccountAppCommand } from "@fabric-types/app-account";
import { Store } from "@ngrx/store";
import { DialogLayoutComponent, NavbarOptions } from "@obg/mat.ui.core";
import {
	CACHE_STORE_MISSING_PASSWORD,
	MISSING_PASSWORD_MESSAGE_CODES,
	MissingPasswordConfigService,
	SKIP_MISSING_PASSWORD_KEY,
} from "@obg/ngx.common";
import {
	AbstractTranslatableSnackBarService,
	AuthTransactionConfigService,
	AuthTransactionSelector,
	CORE_ROUTE,
	CoreState,
	CustomerManagementAction,
	CustomerManagementSelector,
	LoginMethod,
	Message,
	MessageType,
} from "@obg/ngx.core";
import { TranslationGroup } from "@obg/ngx.core.content";
import { CacheFactory, CacheStore, DeviceConfigService, StorageMode, ignoreNil } from "@obg/ngx.global";
import { DocumentRef, TelemetryService } from "@obg/ngx.global.evo";
import { GtmEventAction } from "@obg/ngx.gtm/adaptive";
import { RouteAction } from "@obg/ngx.router";
import { OBG_VALIDATORS } from "@obg/ngx.ui.common";
import { BehaviorSubject, Subscription } from "rxjs";
import { take, tap, withLatestFrom } from "rxjs/operators";

import { SgaSetPasswordFormGroup } from "./sga-set-password.model";

@Component({
	selector: "obg-m-sga-set-password",
	styleUrls: ["./sga-set-password.container.scss"],
	templateUrl: "./sga-set-password.container.html",
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class SgaSetPasswordContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-m-sga-set-password") hostCssClass = true;
	@HostBinding("attr.test-id") testId = "sga-set-password-modal";
	@ViewChild("dialog") dialogLayout: DialogLayoutComponent | undefined;

	readonly form = this.createForm();
	readonly timeoutInMilliSeconds = this.missingPasswordConfigService.getMissingPasswordTimeout();
	readonly maxAgeSkipPasswordCache = this.missingPasswordConfigService.getSkipMissingPasswordMaxAgeCache();
	readonly navbarOptions: NavbarOptions = {
		showCloseButton: true,
		hideBackButton: true,
	};
	readonly isDesktop: boolean;

	isLoading$ = new BehaviorSubject<boolean>(false);
	isCountdownFinished = false;

	showVerification = false;
	verificationView = "default";
	secureStartEnabled = false;

	private data$$ = new Subscription();
	private token: string | undefined;

	private cache: CacheStore;
	private customerId: string | undefined;

	constructor(
		private customerManagementAction: CustomerManagementAction,
		private documentRef: DocumentRef,
		private store: Store<CoreState>,
		private formBuilder: UntypedFormBuilder,
		private changeDetector: ChangeDetectorRef,
		private authTransactionSelector: AuthTransactionSelector,
		private dialogRef: MatDialogRef<SgaSetPasswordContainer>,
		private snackBarService: AbstractTranslatableSnackBarService,
		private missingPasswordConfigService: MissingPasswordConfigService,
		private telemetryService: TelemetryService,
		cacheFactory: CacheFactory,
		deviceConfigService: DeviceConfigService,
		authTransactionConfig: AuthTransactionConfigService
	) {
		this.cache = cacheFactory.get(CACHE_STORE_MISSING_PASSWORD, {
			storageMode: StorageMode.LocalStorage,
		});

		this.isDesktop = deviceConfigService.isDesktop();
		this.secureStartEnabled = authTransactionConfig.getBankIdSecureStartEnabled();
	}

	ngOnInit() {
		const customer$ = this.store.select(CustomerManagementSelector.getCustomer).pipe(
			withLatestFrom(this.store.select(this.authTransactionSelector.getAuthenticationToken())),
			take(1),
			tap(([customer, token]) => {
				this.customerId = customer?.customerId;
				this.form.controls.email.setValue(customer?.subscriptions.email);
				this.token = token;
			})
		);

		this.data$$.add(customer$.subscribe(() => this.changeDetector.markForCheck()));
	}

	ngOnDestroy(): void {
		this.data$$?.unsubscribe();
	}

	closeDialog() {
		this.dialogRef.close();
	}

	skipDialog() {
		this.cache.setItem<boolean>(SKIP_MISSING_PASSWORD_KEY, true, { maxAge: this.maxAgeSkipPasswordCache });
		this.gtmEventAction(GtmEventAction.SkipPassword);
		this.closeDialog();
	}

	submit() {
		if (this.secureStartEnabled) {
			this.showVerification = true;
			const token$$ = this.store
				.select(this.authTransactionSelector.getAuthenticationToken())
				.pipe(
					ignoreNil(),
					tap((token) => {
						this.token = token;
						this.setMissingPassword();
					})
				)
				.subscribe();
			this.data$$.add(token$$);
			return;
		}

		this.setMissingPassword();
	}

	navigateToAccountSettings() {
		this.store.dispatch(RouteAction.navigate(CORE_ROUTE.customerManagement.passwordSettings));
		this.dialogRef.close();
	}

	bankIdVerified() {
		this.showVerification = false;
	}

	private setMissingPassword() {
		if (!this.token) {
			this.showError();
			return;
		}
		this.isLoading$.next(true);
		this.documentRef?.obgEventBus?.dispatch(AccountAppCommand.SetMissingPassword, {
			token: this.token,
			newPassword: this.form.controls.newPassword.value,
			onSuccess: () => {
				this.store.dispatch(
					this.customerManagementAction.updateCustomerCredentialTypes("UsernamePasswordCredential")
				);
				this.snackBarService
					.openSnackBar$(
						{
							message: "sga-set-password.success-message",
							action: "general.close",
							duration: 4000,
							type: MessageType.Success,
						},
						[TranslationGroup.Common]
					)
					.pipe(take(1))
					.subscribe(); // eslint-disable-line rxjs/no-ignored-subscription

				this.gtmEventAction(GtmEventAction.SetNewPassword);
				this.isLoading$.next(false);
				this.closeDialog();
			},
			onError: () => {
				this.showError();
				this.isLoading$.next(false);
			},
		});
	}

	private showError(message?: Message) {
		const errorCode = message?.messageCode || MISSING_PASSWORD_MESSAGE_CODES.setMissingPassword.error;

		this.snackBarService
			.openSnackBar$(
				{
					message: `sga-set-password.${errorCode}`,
					action: "general.close",
					duration: 4000,
					type: MessageType.Error,
				},
				[TranslationGroup.Common]
			)
			.pipe(take(1))
			.subscribe(); // eslint-disable-line rxjs/no-ignored-subscription
	}

	private createForm(): SgaSetPasswordFormGroup {
		return this.formBuilder.group({
			email: this.formBuilder.control({ value: "", disabled: true }),
			newPassword: this.formBuilder.control("", [Validators.required, OBG_VALIDATORS.passwordStrength]),
		}) as SgaSetPasswordFormGroup;
	}

	private gtmEventAction(eventType: GtmEventAction) {
		const gtmEvent: Record<string, any> = {
			GUID: this.customerId,
			LoginMethod: LoginMethod.BankId,
			event: eventType,
		};

		this.telemetryService.track(gtmEvent);
	}
}
